import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Banknote, TrendingUp, Clock, Shield, MapPin, FileText, MessageSquare, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import ShareButton from "@/components/ShareButton";
import ServiceConversionSidebar from "@/components/ServiceConversionSidebar";
import { api, fmtFJD, formatApiError } from "@/lib/api";

const riskColors = {
    low: "bg-fiji-green/10 text-fiji-green",
    medium: "bg-coral/10 text-coral",
    high: "bg-destructive/10 text-destructive",
};

export default function JVDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [jv, setJv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contactOpen, setContactOpen] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        api.get(`/jv/${id}`).then(({ data }) => setJv(data)).catch(() => {}).finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 font-app">Loading…</div>;
    if (!jv) return <div className="max-w-7xl mx-auto px-4 py-20"><h1 className="font-display text-2xl">Joint Venture not found</h1></div>;

    const sendInquiry = async () => {
        if (!user) return navigate("/login");
        if (!msg.trim()) return;
        try {
            await api.post("/messages", { jv_id: id, content: msg });
            toast.success("Inquiry sent via platform.");
            setContactOpen(false);
            setMsg("");
            navigate("/messages");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div data-testid="jv-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Button variant="ghost" onClick={() => navigate(-1)} className="font-app mb-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>

            <div className="grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <Badge className="bg-turquoise/20 text-ocean dark:text-turquoise border-0 uppercase tracking-wider text-[10px]">{jv.project_type}</Badge>
                        <Badge className={`uppercase text-[10px] ${riskColors[jv.risk_profile]}`}>{jv.risk_profile} risk</Badge>
                        {jv.vetting_status === "approved" && <Badge className="bg-fiji-green text-white uppercase text-[10px]">Approved</Badge>}
                        {jv.admin_score && <Badge variant="outline" className="text-[10px]">Admin score: {jv.admin_score}/100</Badge>}
                    </div>

                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight flex-1">{jv.title}</h1>
                        <ShareButton type="jv" id={jv.id} title={jv.title} />
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground font-app mb-8">
                        <MapPin className="h-4 w-4 text-coral" /> {jv.location_region}, {jv.location_island}
                    </div>

                    <Card className="p-6 mb-6 bg-sand/40 dark:bg-secondary/40 border-l-4 border-coral">
                        <h2 className="font-display text-xl font-bold mb-2">Project Vision</h2>
                        <p className="font-app text-foreground/80 leading-relaxed">{jv.project_vision}</p>
                    </Card>

                    <h2 className="font-display text-2xl font-bold mb-4">Financial Projections</h2>
                    <Card className="p-6 mb-8">
                        <p className="font-app text-foreground/80 leading-relaxed whitespace-pre-line">{jv.financial_projections}</p>
                    </Card>

                    <h2 className="font-display text-2xl font-bold mb-4">Documents</h2>
                    <Card className="p-6">
                        {jv.documents?.length > 0 ? (
                            <ul className="space-y-2 font-app">
                                {jv.documents.map((d, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm">
                                        <FileText className="h-4 w-4 text-ocean dark:text-turquoise" /> Document {i + 1}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground font-app">Detailed documents available upon NDA via the platform.</p>
                        )}
                    </Card>
                </div>

                <aside className="lg:col-span-1 space-y-4">
                    <Card className="p-6 sticky top-24">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-app mb-1">Capital required</div>
                        <div className="font-display text-3xl font-bold text-ocean dark:text-turquoise">{fmtFJD(jv.capital_required_fjd)}</div>

                        <div className="grid grid-cols-2 gap-3 mt-6 text-sm font-app">
                            <div className="bg-secondary/60 rounded-md p-3">
                                <TrendingUp className="h-4 w-4 text-fiji-green mb-1" />
                                <div className="text-xs text-muted-foreground">ROI estimate</div>
                                <div className="font-bold text-fiji-green text-lg">{jv.roi_estimate_pct}%</div>
                            </div>
                            <div className="bg-secondary/60 rounded-md p-3">
                                <Clock className="h-4 w-4 text-coral mb-1" />
                                <div className="text-xs text-muted-foreground">Timeline</div>
                                <div className="font-bold text-lg">{jv.timeline_months} mo</div>
                            </div>
                            <div className="bg-secondary/60 rounded-md p-3 col-span-2">
                                <Shield className="h-4 w-4 text-ocean dark:text-turquoise mb-1" />
                                <div className="text-xs text-muted-foreground">Landowner equity</div>
                                <div className="font-bold text-lg">{jv.landowner_equity_pct}%</div>
                            </div>
                        </div>

                        <div className="mt-6 bg-coral/10 border border-coral/30 rounded-md p-3 text-xs font-app text-muted-foreground">
                            Bula Real Estate Fiji is the official intermediary in every Joint Venture. We structure the partnership, hold documents, and protect both sides.
                        </div>

                        <Button onClick={() => setContactOpen(true)} data-testid="jv-inquire-btn" className="w-full mt-4 bg-coral text-white hover:bg-coral-deep">
                            <MessageSquare className="h-4 w-4 mr-2" /> Express interest
                        </Button>
                    </Card>

                    <ServiceConversionSidebar
                        context={{
                            kind: "jv",
                            capitalRequiredFjd: jv.capital_required_fjd,
                            projectType: jv.project_type,
                            ownershipType: jv.land_ownership_type,
                            timelineMonths: jv.timeline_months,
                        }}
                        listingTitle={jv.title}
                        listingUrl={typeof window !== "undefined" ? window.location.href : ""}
                    />
                </aside>
            </div>

            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-display">Express interest in this Joint Venture</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground font-app">Tell us about your investment thesis. The platform will introduce qualified parties.</p>
                    <Textarea data-testid="jv-inquiry-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} placeholder="My fund is interested in tourism developments in Viti Levu..." />
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setContactOpen(false)}>Cancel</Button>
                        <Button onClick={sendInquiry} data-testid="jv-send-inquiry-btn" className="bg-ocean-green text-white">Send via platform</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
